/**
 * Phase 5–20 — Dexie-Index pro Aggregat (Shadow neben merged KV-Blob).
 */

import { getElkheirDb } from './elkheirDb';
import { isPhase23NoKvBlobRuntimeEnabled } from './storagePhase23Meta';
import { purchaseLineEntityId } from '../../utils/purchaseLineEntityId';

export const AGGREGATE_TYPES = {
  SALES: 'sales',
  PURCHASES: 'purchases',
  CUSTOMERS: 'customers',
  LOTS: 'lots',
  PRODUCTS: 'products',
  SUPPLIERS: 'suppliers',
  STOCK_DATA: 'stockData',
  INVOICES: 'invoices',
  ORDERS: 'orders',
  EXPENSES: 'expenses',
  INVOICE_PAYMENTS: 'invoicePayments',
  SUPPLIER_DEBTS: 'supplierDebts',
  CUSTOMER_DEBTS: 'customerDebts',
  CUSTOMER_INVOICES: 'customerInvoices',
  SUPERMARKET_PAYMENTS: 'supermarketPayments',
  SUPERMARKET_INVOICES: 'supermarketInvoices',
  EXPIRED_PRODUCTS: 'expiredProducts',
  NOTIFICATIONS: 'notifications',
  WORKER_SESSIONS: 'workerSessions',
  REPORTS: 'reports',
  DISCREPANCY_MANAGEMENT: 'discrepancyManagement',
  STOCK_LOCATIONS: 'stockLocations',
  PERSISTENT_STOCK_DATA: 'persistentStockData',
  OFFLINE_QUEUE: 'offlineQueue',
  BUSINESS_INFO: 'businessInfo',
  USER_INFO: 'userInfo',
  METADATA: 'metadata',
  STOCK_MOVEMENTS: 'stockMovements'
};

export const STOCK_DATA_ROOT_ENTITY_ID = '_root';

function envFlagEnabled(name) {
  const v = process.env[name];
  return v === 'true' || v === '1';
}

function envFlagDisabled(name) {
  const v = process.env[name];
  return v === 'false' || v === '0';
}

/**
 * Phase 25 — domain_aggregate nicht mehr schreiben/lesen (entity_* ist kanonisch).
 * Aktiv wenn explizit SKIP gesetzt oder NO_KV_BLOB + AUTHORITY + SKIP_BLOB.
 */
export function isDomainAggregateIndexSkipped() {
  if (envFlagEnabled('REACT_APP_DOMAIN_AGGREGATE_SKIP')) return true;
  if (!isPhase23NoKvBlobRuntimeEnabled()) return false;
  if (envFlagDisabled('REACT_APP_ENTITY_STORE_AUTHORITY')) return false;
  if (envFlagDisabled('REACT_APP_ENTITY_STORE_SKIP_BLOB')) return false;
  return true;
}

export function isDomainAggregateIndexEnabled() {
  if (envFlagDisabled('REACT_APP_DOMAIN_AGGREGATE_INDEX')) return false;
  if (isDomainAggregateIndexSkipped()) return false;
  return true;
}

function aggregateRowId(businessId, aggregate, entityId) {
  return `${businessId}:${aggregate}:${entityId}`;
}

function idOf(obj, ...keys) {
  for (const key of keys) {
    const v = obj?.[key];
    if (v != null && v !== '') return String(v);
  }
  return '';
}

const entityIdFns = {
  [AGGREGATE_TYPES.SALES]: (s) => idOf(s, 'id', 'orderId'),
  [AGGREGATE_TYPES.PURCHASES]: (p) => purchaseLineEntityId(p),
  [AGGREGATE_TYPES.CUSTOMERS]: (c) => idOf(c, 'customerId', 'id'),
  [AGGREGATE_TYPES.LOTS]: (l) => idOf(l, 'id'),
  [AGGREGATE_TYPES.PRODUCTS]: (p) => idOf(p, 'id', 'productId'),
  [AGGREGATE_TYPES.SUPPLIERS]: (s) => idOf(s, 'id', 'supplierId'),
  [AGGREGATE_TYPES.INVOICES]: (i) => idOf(i, 'id', 'invoiceNumber'),
  [AGGREGATE_TYPES.ORDERS]: (o) => idOf(o, 'id', 'orderId'),
  [AGGREGATE_TYPES.EXPENSES]: (e) => idOf(e, 'id', 'expenseId'),
  [AGGREGATE_TYPES.INVOICE_PAYMENTS]: (p) => idOf(p, 'id', 'paymentId'),
  [AGGREGATE_TYPES.SUPPLIER_DEBTS]: (d) => idOf(d, 'id', 'supplierId'),
  [AGGREGATE_TYPES.CUSTOMER_DEBTS]: (d) => idOf(d, 'id', 'customerId'),
  [AGGREGATE_TYPES.CUSTOMER_INVOICES]: (i) => idOf(i, 'id', 'invoiceNumber'),
  [AGGREGATE_TYPES.SUPERMARKET_PAYMENTS]: (p) => idOf(p, 'id', 'paymentId'),
  [AGGREGATE_TYPES.SUPERMARKET_INVOICES]: (i) => idOf(i, 'id', 'invoiceNumber'),
  [AGGREGATE_TYPES.EXPIRED_PRODUCTS]: (e) => idOf(e, 'id', 'lotId'),
  [AGGREGATE_TYPES.NOTIFICATIONS]: (n) => idOf(n, 'id', 'notificationId'),
  [AGGREGATE_TYPES.WORKER_SESSIONS]: (s) => idOf(s, 'id', 'sessionId'),
  [AGGREGATE_TYPES.PERSISTENT_STOCK_DATA]: (i) => idOf(i, 'id', 'productId', 'locationId'),
  [AGGREGATE_TYPES.OFFLINE_QUEUE]: (i) => idOf(i, 'id', 'outboxId', 'timestamp'),
  [AGGREGATE_TYPES.STOCK_MOVEMENTS]: (m) => idOf(m, 'movementId', 'id')
};

const updatedAtFns = {
  [AGGREGATE_TYPES.SALES]: (s) => s.updatedAt || s.date,
  [AGGREGATE_TYPES.PURCHASES]: (p) => p.updatedAt || p.date,
  [AGGREGATE_TYPES.CUSTOMERS]: (c) => c.updatedAt,
  [AGGREGATE_TYPES.LOTS]: (l) => l.updatedAt,
  [AGGREGATE_TYPES.PRODUCTS]: (p) => p.updatedAt,
  [AGGREGATE_TYPES.SUPPLIERS]: (s) => s.updatedAt,
  [AGGREGATE_TYPES.INVOICES]: (i) => i.updatedAt || i.invoiceDate,
  [AGGREGATE_TYPES.ORDERS]: (o) => o.updatedAt || o.date,
  [AGGREGATE_TYPES.EXPENSES]: (e) => e.updatedAt || e.date,
  [AGGREGATE_TYPES.INVOICE_PAYMENTS]: (p) => p.updatedAt || p.date,
  [AGGREGATE_TYPES.SUPPLIER_DEBTS]: (d) => d.updatedAt,
  [AGGREGATE_TYPES.CUSTOMER_DEBTS]: (d) => d.updatedAt,
  [AGGREGATE_TYPES.CUSTOMER_INVOICES]: (i) => i.updatedAt || i.invoiceDate,
  [AGGREGATE_TYPES.SUPERMARKET_PAYMENTS]: (p) => p.updatedAt || p.date,
  [AGGREGATE_TYPES.SUPERMARKET_INVOICES]: (i) => i.updatedAt || i.invoiceDate,
  [AGGREGATE_TYPES.EXPIRED_PRODUCTS]: (e) => e.updatedAt || e.expiryDate,
  [AGGREGATE_TYPES.NOTIFICATIONS]: (n) => n.updatedAt || n.createdAt,
  [AGGREGATE_TYPES.WORKER_SESSIONS]: (s) => s.updatedAt || s.loginTime,
  [AGGREGATE_TYPES.PERSISTENT_STOCK_DATA]: (i) => i.updatedAt,
  [AGGREGATE_TYPES.OFFLINE_QUEUE]: (i) => i.updatedAt || i.timestamp,
  [AGGREGATE_TYPES.STOCK_MOVEMENTS]: (m) => m.updatedAt || m.createdAt
};

const ARRAY_PAYLOAD_FIELDS = {
  [AGGREGATE_TYPES.SALES]: 'sales',
  [AGGREGATE_TYPES.PURCHASES]: 'purchases',
  [AGGREGATE_TYPES.CUSTOMERS]: 'customers',
  [AGGREGATE_TYPES.LOTS]: 'lots',
  [AGGREGATE_TYPES.PRODUCTS]: 'products',
  [AGGREGATE_TYPES.SUPPLIERS]: 'suppliers',
  [AGGREGATE_TYPES.INVOICES]: 'invoices',
  [AGGREGATE_TYPES.ORDERS]: 'orders',
  [AGGREGATE_TYPES.EXPENSES]: 'expenses',
  [AGGREGATE_TYPES.INVOICE_PAYMENTS]: 'invoicePayments',
  [AGGREGATE_TYPES.SUPPLIER_DEBTS]: 'supplierDebts',
  [AGGREGATE_TYPES.CUSTOMER_DEBTS]: 'customerDebts',
  [AGGREGATE_TYPES.CUSTOMER_INVOICES]: 'customerInvoices',
  [AGGREGATE_TYPES.SUPERMARKET_PAYMENTS]: 'supermarketPayments',
  [AGGREGATE_TYPES.SUPERMARKET_INVOICES]: 'supermarketInvoices',
  [AGGREGATE_TYPES.EXPIRED_PRODUCTS]: 'expiredProducts',
  [AGGREGATE_TYPES.NOTIFICATIONS]: 'notifications',
  [AGGREGATE_TYPES.WORKER_SESSIONS]: 'workerSessions',
  [AGGREGATE_TYPES.PERSISTENT_STOCK_DATA]: 'persistentStockData',
  [AGGREGATE_TYPES.OFFLINE_QUEUE]: 'offlineQueue',
  [AGGREGATE_TYPES.STOCK_MOVEMENTS]: 'stockMovements'
};

const ROOT_DOCUMENT_FIELDS = {
  [AGGREGATE_TYPES.STOCK_DATA]: 'stockData',
  [AGGREGATE_TYPES.REPORTS]: 'reports',
  [AGGREGATE_TYPES.DISCREPANCY_MANAGEMENT]: 'discrepancyManagement',
  [AGGREGATE_TYPES.BUSINESS_INFO]: 'businessInfo',
  [AGGREGATE_TYPES.USER_INFO]: 'userInfo',
  [AGGREGATE_TYPES.METADATA]: 'metadata'
};

function hasRootDocumentContent(doc) {
  return doc && typeof doc === 'object' && Object.keys(doc).length > 0;
}

function normalizeArrayField(raw) {
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === 'object') return Object.values(raw);
  return [];
}

async function pruneAggregateIndex(businessId, aggregate, keepEntityIds, db) {
  if (!isDomainAggregateIndexEnabled() || !businessId || !db.domain_aggregate) return;

  const bid = String(businessId);
  const rows = await db.domain_aggregate
    .where('[businessId+aggregate]')
    .equals([bid, aggregate])
    .toArray();
  const keep =
    keepEntityIds instanceof Set ? keepEntityIds : new Set(keepEntityIds);
  const staleIds = rows
    .filter((row) => !keep.has(String(row.entityId)))
    .map((row) => row.id);
  if (staleIds.length > 0) {
    await db.domain_aggregate.bulkDelete(staleIds);
  }
}

function pushRow(rows, bid, aggregate, entityId, payload, updatedAt) {
  rows.push({
    id: aggregateRowId(bid, aggregate, entityId),
    businessId: bid,
    aggregate,
    entityId,
    payload,
    updatedAt: updatedAt || new Date().toISOString()
  });
}

async function syncArrayAggregate(bid, fieldSet, aggregate, payload, rows, db) {
  if (!fieldSet.has(aggregate)) return;

  const field = ARRAY_PAYLOAD_FIELDS[aggregate];
  const raw = payload[field];
  const items =
    field === 'purchases' || field === 'lots' || field === 'stockMovements'
      ? normalizeArrayField(raw)
      : Array.isArray(raw)
        ? raw
        : [];

  if (
    field !== 'purchases' &&
    field !== 'lots' &&
    field !== 'stockMovements' &&
    !Array.isArray(raw)
  ) {
    await pruneAggregateIndex(bid, aggregate, new Set(), db);
    return;
  }
  if (items.length === 0 && raw == null) {
    await pruneAggregateIndex(bid, aggregate, new Set(), db);
    return;
  }

  const idFn = entityIdFns[aggregate];
  const updatedFn = updatedAtFns[aggregate];
  const keepIds = new Set();
  for (const item of items) {
    const entityId = idFn(item);
    if (!entityId) continue;
    keepIds.add(entityId);
    pushRow(rows, bid, aggregate, entityId, item, updatedFn(item));
  }
  await pruneAggregateIndex(bid, aggregate, keepIds, db);
}

async function syncRootAggregate(bid, fieldSet, aggregate, payload, rows, db) {
  if (!fieldSet.has(aggregate)) return;

  const field = ROOT_DOCUMENT_FIELDS[aggregate];
  const doc = payload[field];
  if (!hasRootDocumentContent(doc)) {
    await pruneAggregateIndex(bid, aggregate, new Set(), db);
    return;
  }
  pushRow(
    rows,
    bid,
    aggregate,
    STOCK_DATA_ROOT_ENTITY_ID,
    doc,
    payload.payloadUpdatedAt
  );
  await pruneAggregateIndex(bid, aggregate, new Set([STOCK_DATA_ROOT_ENTITY_ID]), db);
}

async function syncStockLocationsAggregate(bid, fieldSet, payload, rows, db) {
  if (!fieldSet.has(AGGREGATE_TYPES.STOCK_LOCATIONS)) return;

  const raw = payload.stockLocations;
  if (!Array.isArray(raw) || raw.length === 0) {
    await pruneAggregateIndex(bid, AGGREGATE_TYPES.STOCK_LOCATIONS, new Set(), db);
    return;
  }
  const doc = { locations: raw };
  pushRow(
    rows,
    bid,
    AGGREGATE_TYPES.STOCK_LOCATIONS,
    STOCK_DATA_ROOT_ENTITY_ID,
    doc,
    payload.payloadUpdatedAt
  );
  await pruneAggregateIndex(
    bid,
    AGGREGATE_TYPES.STOCK_LOCATIONS,
    new Set([STOCK_DATA_ROOT_ENTITY_ID]),
    db
  );
}

/**
 * @param {string} businessId
 * @param {object} payload
 * @param {string[]} [fields]
 * @param {import('dexie').Dexie} [db]
 */
export async function syncAggregateIndexFromPayload(businessId, payload, fields, db = getElkheirDb()) {
  if (!isDomainAggregateIndexEnabled() || !businessId || !payload || !db.domain_aggregate) return;

  const bid = String(businessId);
  const fieldSet = new Set(fields || Object.values(AGGREGATE_TYPES));
  const rows = [];

  for (const aggregate of Object.keys(ARRAY_PAYLOAD_FIELDS)) {
    await syncArrayAggregate(bid, fieldSet, aggregate, payload, rows, db);
  }
  for (const aggregate of Object.keys(ROOT_DOCUMENT_FIELDS)) {
    await syncRootAggregate(bid, fieldSet, aggregate, payload, rows, db);
  }
  await syncStockLocationsAggregate(bid, fieldSet, payload, rows, db);

  if (rows.length > 0) {
    await db.domain_aggregate.bulkPut(rows);
  }
}

export async function indexSaleInAggregateStore(businessId, sale) {
  if (!isDomainAggregateIndexEnabled() || !businessId || !sale) return;
  const entityId = entityIdFns[AGGREGATE_TYPES.SALES](sale);
  if (!entityId) return;

  const db = getElkheirDb();
  if (!db.domain_aggregate) return;

  const bid = String(businessId);
  await db.domain_aggregate.put({
    id: aggregateRowId(bid, AGGREGATE_TYPES.SALES, entityId),
    businessId: bid,
    aggregate: AGGREGATE_TYPES.SALES,
    entityId,
    payload: sale,
    updatedAt: sale.updatedAt || sale.date || new Date().toISOString()
  });
}

export async function removeSaleFromAggregateStore(businessId, saleId) {
  if (!isDomainAggregateIndexEnabled() || !businessId || !saleId) return;
  const db = getElkheirDb();
  if (!db.domain_aggregate) return;

  const bid = String(businessId);
  const sid = String(saleId);
  await db.domain_aggregate.delete(aggregateRowId(bid, AGGREGATE_TYPES.SALES, sid));
  if (sid.startsWith('sale_')) {
    await db.domain_aggregate.delete(
      aggregateRowId(bid, AGGREGATE_TYPES.SALES, sid.replace(/^sale_/, ''))
    );
  }
}

export async function removePurchaseFromAggregateStore(businessId, purchaseId) {
  if (!isDomainAggregateIndexEnabled() || !businessId || !purchaseId) return;
  const db = getElkheirDb();
  if (!db.domain_aggregate) return;

  const bid = String(businessId);
  await db.domain_aggregate.delete(
    aggregateRowId(bid, AGGREGATE_TYPES.PURCHASES, String(purchaseId))
  );
}

async function listAggregateEntities(businessId, aggregate) {
  if (!businessId) return [];
  const db = getElkheirDb();
  if (!db.domain_aggregate) return [];

  const rows = await db.domain_aggregate
    .where('[businessId+aggregate]')
    .equals([String(businessId), aggregate])
    .toArray();
  return rows.map((r) => r.payload).filter(Boolean);
}

async function getAggregateRootDocument(businessId, aggregate) {
  if (!businessId) return {};
  const db = getElkheirDb();
  if (!db.domain_aggregate) return {};

  const row = await db.domain_aggregate.get(
    aggregateRowId(String(businessId), aggregate, STOCK_DATA_ROOT_ENTITY_ID)
  );
  return row?.payload && typeof row.payload === 'object' ? row.payload : {};
}

function listExport(name, aggregate) {
  return async function listFn(businessId) {
    return listAggregateEntities(businessId, aggregate);
  };
}

export const listAggregateSales = listExport('sales', AGGREGATE_TYPES.SALES);
export const listAggregatePurchases = listExport('purchases', AGGREGATE_TYPES.PURCHASES);
export const listAggregateLots = listExport('lots', AGGREGATE_TYPES.LOTS);
export const listAggregateCustomers = listExport('customers', AGGREGATE_TYPES.CUSTOMERS);
export const listAggregateProducts = listExport('products', AGGREGATE_TYPES.PRODUCTS);
export const listAggregateSuppliers = listExport('suppliers', AGGREGATE_TYPES.SUPPLIERS);
export const listAggregateInvoices = listExport('invoices', AGGREGATE_TYPES.INVOICES);
export const listAggregateOrders = listExport('orders', AGGREGATE_TYPES.ORDERS);
export const listAggregateExpenses = listExport('expenses', AGGREGATE_TYPES.EXPENSES);
export const listAggregateInvoicePayments = listExport(
  'invoicePayments',
  AGGREGATE_TYPES.INVOICE_PAYMENTS
);
export const listAggregateSupplierDebts = listExport(
  'supplierDebts',
  AGGREGATE_TYPES.SUPPLIER_DEBTS
);
export const listAggregateCustomerDebts = listExport(
  'customerDebts',
  AGGREGATE_TYPES.CUSTOMER_DEBTS
);
export const listAggregateCustomerInvoices = listExport(
  'customerInvoices',
  AGGREGATE_TYPES.CUSTOMER_INVOICES
);
export const listAggregateSupermarketPayments = listExport(
  'supermarketPayments',
  AGGREGATE_TYPES.SUPERMARKET_PAYMENTS
);
export const listAggregateSupermarketInvoices = listExport(
  'supermarketInvoices',
  AGGREGATE_TYPES.SUPERMARKET_INVOICES
);
export const listAggregateExpiredProducts = listExport(
  'expiredProducts',
  AGGREGATE_TYPES.EXPIRED_PRODUCTS
);
export const listAggregateNotifications = listExport(
  'notifications',
  AGGREGATE_TYPES.NOTIFICATIONS
);
export const listAggregateWorkerSessions = listExport(
  'workerSessions',
  AGGREGATE_TYPES.WORKER_SESSIONS
);
export const listAggregatePersistentStockData = listExport(
  'persistentStockData',
  AGGREGATE_TYPES.PERSISTENT_STOCK_DATA
);
export const listAggregateOfflineQueue = listExport(
  'offlineQueue',
  AGGREGATE_TYPES.OFFLINE_QUEUE
);
export const listAggregateStockMovements = listExport(
  'stockMovements',
  AGGREGATE_TYPES.STOCK_MOVEMENTS
);

export async function getAggregateStockData(businessId) {
  return getAggregateRootDocument(businessId, AGGREGATE_TYPES.STOCK_DATA);
}
export async function getAggregateReports(businessId) {
  return getAggregateRootDocument(businessId, AGGREGATE_TYPES.REPORTS);
}
export async function getAggregateDiscrepancyManagement(businessId) {
  return getAggregateRootDocument(businessId, AGGREGATE_TYPES.DISCREPANCY_MANAGEMENT);
}
export async function getAggregateBusinessInfo(businessId) {
  const doc = await getAggregateRootDocument(businessId, AGGREGATE_TYPES.BUSINESS_INFO);
  return hasRootDocumentContent(doc) ? doc : null;
}
export async function getAggregateUserInfo(businessId) {
  const doc = await getAggregateRootDocument(businessId, AGGREGATE_TYPES.USER_INFO);
  return hasRootDocumentContent(doc) ? doc : null;
}
export async function getAggregateMetadata(businessId) {
  return getAggregateRootDocument(businessId, AGGREGATE_TYPES.METADATA);
}
export async function getAggregateStockLocations(businessId) {
  const doc = await getAggregateRootDocument(businessId, AGGREGATE_TYPES.STOCK_LOCATIONS);
  return Array.isArray(doc?.locations) ? doc.locations : [];
}

export async function countAggregateByType(businessId) {
  if (!businessId) return {};
  const db = getElkheirDb();
  if (!db.domain_aggregate) return {};

  const bid = String(businessId);
  const counts = {};
  for (const aggregate of Object.values(AGGREGATE_TYPES)) {
    counts[aggregate] = await db.domain_aggregate
      .where('[businessId+aggregate]')
      .equals([bid, aggregate])
      .count();
  }
  return counts;
}

/**
 * Phase 25 — Shadow-Index-Zeilen für ein Business löschen.
 * @param {string} businessId
 * @param {import('dexie').Dexie} [db]
 */
export async function purgeDomainAggregateForBusiness(businessId, db = null) {
  if (!businessId) return { deleted: 0 };
  const database = db || getElkheirDb();
  if (!database.isOpen()) {
    await database.open();
  }
  if (!database.domain_aggregate) return { deleted: 0 };

  const bid = String(businessId);
  let deleted = 0;
  for (const aggregate of Object.values(AGGREGATE_TYPES)) {
    deleted += await database.domain_aggregate
      .where('[businessId+aggregate]')
      .equals([bid, aggregate])
      .delete();
  }
  return { deleted, businessId: bid };
}
